import { SYSTEM_VERSION } from "../version";

/**
 * Consulta /version.json com cache-busting para verificar se há atualização no servidor.
 * Retorna { hasUpdate: boolean, serverVersion: string|null, currentVersion: string }
 */
export async function checkServerVersion() {
  const storedVersion = typeof localStorage !== "undefined" ? localStorage.getItem("system_version") : null;
  const currentVersion = storedVersion || SYSTEM_VERSION || "unknown";

  try {
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), 3500) : null;

    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache"
      },
      signal: controller ? controller.signal : undefined
    });

    if (timeoutId) clearTimeout(timeoutId);

    if (res && res.ok) {
      const data = await res.json();
      const serverVersion = data && data.version ? data.version : null;

      if (serverVersion && serverVersion !== currentVersion) {
        return {
          hasUpdate: true,
          serverVersion,
          currentVersion
        };
      }

      return {
        hasUpdate: false,
        serverVersion,
        currentVersion
      };
    }
  } catch (err) {
    // Falha silenciosa de rede/timeout para nunca travar a aplicação
    console.warn(">> [versionChecker] Não foi possível verificar /version.json:", err.message);
  }

  return {
    hasUpdate: false,
    serverVersion: null,
    currentVersion
  };
}

/**
 * Limpa caches do navegador e recarrega a página de forma limpa.
 * @param {string} [targetUrl] - URL de destino opcional (ex: "/tickets"). Se omitido, recarrega a URL atual.
 * @param {string} [newVersion] - Versão nova a registrar no localStorage
 * @param {number} [delay=50] - Atraso em milissegundos antes do reload
 */
export async function applyUpdateAndReload(targetUrl, newVersion, delay = 50) {
  try {
    if (newVersion && typeof localStorage !== "undefined") {
      localStorage.setItem("system_version", newVersion);
    }

    // 1. Limpa CacheStorage (HTML, scripts antigos, etc)
    if (typeof window !== "undefined" && "caches" in window) {
      try {
        const names = await window.caches.keys();
        await Promise.all(names.map((name) => window.caches.delete(name)));
      } catch (_) {}
    }

    // 2. Avisa Service Worker para forçar atualização
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.update().catch(() => {});
        }
      } catch (_) {}
    }
  } catch (err) {
    console.warn(">> [versionChecker] Erro ao limpar cache antes de recarregar:", err);
  }

  const navigate = () => {
    if (targetUrl) {
      window.location.href = targetUrl;
    } else {
      window.location.reload(true);
    }
  };

  if (delay <= 0) {
    navigate();
  } else {
    setTimeout(navigate, delay);
  }
}
